/**
 * CRM Lead Inbox / Patient database
 *
 * Two modes of the same page:
 *  - inbox (default, /crm): recruiter triage inbox for the lead pipeline with
 *    a List | Board view toggle (persisted via getPref/setPref). Board view
 *    renders a per-stage KanbanBoard fed by the current filters; list view is
 *    the classic table/cards with pipeline tabs and bulk actions.
 *  - database (/crm/database): the all-patients directory — same search,
 *    filter panel, sort, bulk bar, and table/cards, but no pipeline tabs,
 *    tasks panel, view toggle, or test tools. Archived rows get a gray chip
 *    in both modes.
 *
 * Pipeline tabs are driven by GET /contacts/stats; the list maps 1:1 to
 * GET /contacts params (q, lifecycle, eligibility, study, owner, unassigned,
 * sort, and the lead_answers filter: answer_question + answer_op +
 * answer_value). All filters auto-apply (text inputs debounced 300ms). Rows
 * show study chips, status, inline owner assignment, and the last
 * communication (with a needs-your-reply indicator), and open the contact
 * detail (/crm/contacts/:id). Bulk assign / set-status PATCHes each selected
 * contact sequentially. A MyTasksPanel above the tabs surfaces the recruiter's
 * due-today/overdue follow-ups. Super-admin keeps the Phase 2 test tools
 * (seed flows / reconciliation / automation tick) in a menu.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  FlaskConical,
  Inbox,
  LayoutGrid,
  List,
  Loader2,
  MoonStar,
  RefreshCw,
  Send,
  UserPlus,
  Users,
  Workflow,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ContactListItem, LifecycleStats, StudyOption, contactName, studyInternalLabel } from '../types';
import { INCOMING_ELIGIBILITY_OPTIONS, FINAL_DISPOSITIONS, LIFECYCLES, MILESTONE_FILTER_OPTIONS, MILESTONE_LABELS, STUDY_ATTRIBUTED_STAGES, dateTime, formatPhone, getPref, needsReply, notifyStatsChanged, prettySlug, relTime, setPref, useTeam } from '../lib';
import { ActiveFilters, AddFilterMenu, FilterBar, FilterChip, FilterSelect, LifecycleBadge, MergeCandidatesModal, MilestoneChip, MyTasksPanel, OwnerSelect, PageHeader, RefreshButton, SearchInput, SegmentedControl, SegmentedTabs, SnoozeButton, isActiveSnooze } from '../components';
import type { ActiveChip, FilterDef, MergeCandidate } from '../components';
import { ChannelIcon, StudyChips } from '../components/shared';
import KanbanBoard from '../components/KanbanBoard';
import NewLeadModal from '../components/NewLeadModal';
import DispositionReasonModal from '../components/DispositionReasonModal';
import StageStudyConfirmModal from '../components/StageStudyConfirmModal';

const LIMIT = 50;
const ANSWER_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains'];
const UNASSIGNED = '__unassigned__';

interface Filters {
  q: string;
  lifecycle: string;
  eligibility: string;
  /** Derived pertinent-milestone key ('' = any). */
  milestone: string;
  study: string;
  /** Owner user id ('' = any). Mutually exclusive with `unassigned`. */
  owner: string;
  unassigned: boolean;
  /** Inbox list only: flip the list to the archived-only set (conversation=archived). */
  showArchived: boolean;
  /** Inbox/board only: reveal leads snoozed to a future time (hidden by default). */
  showSnoozed: boolean;
  sort: string; // '' = newest (default), 'oldest', 'activity' (last_activity_at desc), 'eligibility' (incoming verdict)
  answerQuestion: string;
  answerOp: string;
  answerValue: string;
  /** Medical-fact cohort filters (profile tables). condition = name or profile_key. */
  condition: string;
  labTest: string;
  labOp: string;
  labValue: string;
  /** Only count recruiter-verified facts for the condition/lab filters. */
  verifiedOnly: boolean;
}

// Numeric comparison ops for the lab/vital filter (mirrors backend LAB_OPS).
const LAB_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];

const DEFAULT_FILTERS: Filters = {
  q: '',
  lifecycle: '',
  eligibility: '',
  milestone: '',
  study: '',
  owner: '',
  unassigned: false,
  showArchived: false,
  showSnoozed: false,
  sort: '',
  answerQuestion: '',
  answerOp: 'eq',
  answerValue: '',
  condition: '',
  labTest: '',
  labOp: 'gt',
  labValue: '',
  verifiedOnly: false,
};

// localStorage pref key (namespaced under `crm.` by getPref/setPref). Persisted
// per mode so the inbox and the patient database keep their own filter sets —
// switching between them, or navigating away and back, restores each one.
const FILTERS_PREF = 'contacts.filters';

/** Load this mode's persisted filters, merged over the defaults. */
function loadFilters(mode: 'inbox' | 'database'): Filters {
  const raw = getPref(`${FILTERS_PREF}.${mode}`, '');
  if (!raw) return DEFAULT_FILTERS;
  try {
    return { ...DEFAULT_FILTERS, ...(JSON.parse(raw) as Partial<Filters>) };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function buildParams(f: Filters, offset: number, hideArchived: boolean): Record<string, string | number> {
  const p: Record<string, string | number> = { limit: LIMIT, offset };
  // Archived-only view (inbox chip): the backend ignores hide_archived for this
  // tab. Otherwise the leads inbox hides archived leads; the patient database
  // (hideArchived === false) shows everyone.
  if (f.showArchived) {
    p.conversation = 'archived';
  } else if (hideArchived) {
    p.hide_archived = 'true';
  }
  // The inbox/board hide future-snoozed leads unless "Show snoozed" is on; the
  // patient database (hideArchived === false) always lists everyone.
  p.show_snoozed = hideArchived && !f.showSnoozed ? 'false' : 'true';
  if (f.q.trim()) p.q = f.q.trim();
  if (f.lifecycle.trim()) p.lifecycle = f.lifecycle.trim();
  if (f.eligibility.trim()) p.eligibility = f.eligibility.trim();
  if (f.milestone.trim()) p.milestone = f.milestone.trim();
  if (f.study.trim()) p.study = f.study.trim();
  if (f.owner.trim()) p.owner = f.owner.trim();
  if (f.unassigned) p.unassigned = 'true';
  if (f.sort) p.sort = f.sort;
  // Only send the answer filter once BOTH halves are present — a question id
  // without a value would 400 on numeric ops (and match nothing on eq) while
  // the recruiter is still typing.
  if (f.answerQuestion.trim() && f.answerValue.trim()) {
    p.answer_question = f.answerQuestion.trim();
    p.answer_op = f.answerOp;
    p.answer_value = f.answerValue.trim();
  }
  // Medical-fact cohort filters: a condition name/profile_key and/or a lab value.
  if (f.condition.trim()) p.condition = f.condition.trim();
  if (f.labTest.trim()) {
    p.lab = f.labTest.trim();
    // A value is optional ("has a BMI at all" vs "BMI > 30").
    if (f.labValue.trim()) {
      p.lab_op = f.labOp;
      p.lab_value = f.labValue.trim();
    }
  }
  if (f.verifiedOnly) p.verified_only = 'true';
  return p;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** First (earliest) lead source for a row's subline. */
const sourceOf = (c: ContactListItem): string | null => {
  const src = (c.studies || []).find((s) => s.lead_source)?.lead_source;
  return src ? prettySlug(src) : null;
};

/**
 * Most recent lead arrival = the max first_seen_at across the contact's leads,
 * or null when it has none. The backend sorts studies ascending by
 * first_seen_at, so the LAST is the most recent submission; the max is computed
 * defensively in case that order ever changes. Intentionally the max (not the
 * earliest) so a re-submitted lead — which inserts a new contact_leads row —
 * shows its new arrival time, per the owner clarification.
 */
const arrivalOf = (c: ContactListItem): string | null => {
  const s = c.studies;
  if (!s || s.length === 0) return null;
  return s.reduce<string | null>(
    (max, x) => (x.first_seen_at && (!max || x.first_seen_at > max) ? x.first_seen_at : max),
    null,
  );
};

export default function Contacts({ mode = 'inbox' }: { mode?: 'inbox' | 'database' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { team } = useTeam();
  const isSuperAdmin = user?.role === 'super_admin';
  const isDatabase = mode === 'database';

  const [rows, setRows] = useState<ContactListItem[]>([]);
  const [stats, setStats] = useState<LifecycleStats | null>(null);
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [mergeCandidates, setMergeCandidates] = useState<MergeCandidate[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters — one state object; filtersRef always holds the latest value so
  // runQuery never reads stale state when called right after a change. Seeded
  // from this mode's persisted set (apply() mirrors every change back).
  const [filters, setFilters] = useState<Filters>(() => loadFilters(mode));
  const filtersRef = useRef<Filters>(filters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Manual "New lead" create flow (item 9).
  const [showNewLead, setShowNewLead] = useState(false);
  const handleLeadCreated = (contactId: string) => {
    setShowNewLead(false);
    notifyStatsChanged(); // sidebar/tab counts refetch
    navigate(`/crm/contacts/${contactId}`);
  };

  // Selection / bulk actions
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Inbox view (list | board), persisted per browser. Database mode is always list.
  const [view, setView] = useState<'list' | 'board'>(() =>
    getPref('inbox.view', 'list') === 'board' ? 'board' : 'list',
  );
  const isBoard = !isDatabase && view === 'board';
  // Mirror for the stable mount/mode effects so they read the current view
  // without re-subscribing on every toggle.
  const isBoardRef = useRef(isBoard);
  isBoardRef.current = isBoard;
  // Mirror so the stable runQuery (deps []) reads the current mode: the inbox
  // hides archived leads, the database shows everyone.
  const isDatabaseRef = useRef(isDatabase);
  isDatabaseRef.current = isDatabase;
  // Bumped by the header Refresh button so the board refetches its columns too.
  const [boardRefresh, setBoardRefresh] = useState(0);

  // Super-admin test tools
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // Monotonic request id: a response only commits if it is still the newest
  // query, so a slow earlier fetch can never clobber a newer filter's results
  // and an in-flight Load-more can't append rows from a previous filter.
  const reqSeqRef = useRef(0);

  const runQuery = useCallback(async (offset: number, replace: boolean) => {
    const seq = ++reqSeqRef.current;
    if (replace) {
      setLoading(true);
      setError(null);
      setSelected(new Set());
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await api.get('/api/crm/contacts', { params: buildParams(filtersRef.current, offset, !isDatabaseRef.current) });
      if (seq !== reqSeqRef.current) return; // stale response — a newer query owns the list
      const batch: ContactListItem[] = res.data?.contacts || [];
      setRows((prev) => {
        if (replace) return batch;
        // Dedup on append: a lead created between pages shifts the offset and
        // would repeat the page-boundary rows (duplicate React keys).
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...batch.filter((b) => !seen.has(b.id))];
      });
      setHasMore(batch.length === LIMIT);
      setTruncated(!!res.data?.truncated);
    } catch (e: any) {
      if (seq === reqSeqRef.current) setError(errMsg(e));
    } finally {
      if (seq === reqSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      // Match the list: the inbox list hides snoozed by default, so its tab/total
      // counts should too. The board (columns include snoozed), the database, and
      // "Show snoozed" all request the full counts. (active/awaiting are always
      // snooze-filtered server-side, independent of this.)
      const showSnoozed =
        isBoardRef.current || isDatabaseRef.current || filtersRef.current.showSnoozed ? 'true' : 'false';
      const params: Record<string, string> = { show_snoozed: showSnoozed };
      // The working pipeline views (inbox list default + board columns) hide
      // archived leads from their rows/cards, so their tab/column badges must
      // exclude archived too — otherwise "Screened (13)" sits over 2 visible
      // cards. The patient database always shows everyone, and the list's
      // archived-only "Show archived" view wants the full counts.
      if (!isDatabaseRef.current && !filtersRef.current.showArchived) params.hide_archived = 'true';
      const res = await api.get('/api/crm/contacts/stats', { params });
      setStats(res.data || null);
    } catch {
      // Tolerate silently — tabs render without counts.
    }
  }, []);

  // Re-pullable so Refresh and reconciliation (which flags new candidates) keep
  // the banner in sync, not just the initial mount.
  const loadMergeCandidates = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/merge-candidates');
      setMergeCandidates(res.data?.merge_candidates || []);
    } catch {
      // Tolerate silently — the banner just won't show.
    }
  }, []);

  useEffect(() => {
    // Board view never renders the list, so skip its hidden 50-row fetch —
    // switching back to list always refetches (see switchView).
    if (isBoardRef.current) setLoading(false);
    else runQuery(0, true);
    loadStats();
    loadMergeCandidates();
    api.get('/api/crm/studies').then((r) => setStudies(r.data?.studies || [])).catch(() => {});
    const timerRef = debounceRef; // stable ref object — read .current at cleanup time
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [runQuery, loadStats, loadMergeCandidates]);

  // /crm (inbox) and /crm/database both render this component, so React keeps
  // the instance across the route switch — load the target mode's persisted
  // filters (each mode keeps its own), clear selection, and refetch.
  const modeRef = useRef(mode);
  useEffect(() => {
    if (modeRef.current === mode) return;
    modeRef.current = mode;
    const f = loadFilters(mode);
    filtersRef.current = f;
    setFilters(f);
    setSelected(new Set());
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isBoardRef.current) runQuery(0, true);
  }, [mode, runQuery]);

  /** Apply a filter change and re-query page 0 (text inputs debounce 300ms). */
  const apply = (patch: Partial<Filters>, debounce = false) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    setPref(`${FILTERS_PREF}.${mode}`, JSON.stringify(next)); // survive navigation
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Board view reads `filters` directly (KanbanBoard refetches its own
    // columns) — the hidden list query would be wasted there.
    if (isBoard) return;
    if (debounce) {
      debounceRef.current = setTimeout(() => runQuery(0, true), 300);
    } else {
      runQuery(0, true);
    }
  };

  // Reset the list AND the counts: clearing showSnoozed changes what the stats
  // request returns, and apply() only re-runs the list query.
  const clearFilters = () => { apply({ ...DEFAULT_FILTERS }); loadStats(); };
  const loadMore = () => runQuery(rows.length, false);
  const refresh = () => {
    if (!isBoard) runQuery(0, true); // board refetches via the token below
    loadStats();
    loadMergeCandidates(); // reconciliation (runSync) flags new candidates
    setBoardRefresh((n) => n + 1);
  };

  const switchView = (v: 'list' | 'board') => {
    if (v === view) return;
    setView(v);
    setPref('inbox.view', v);
    if (v === 'board') {
      setSelected(new Set()); // bulk selection is list-only
      if (debounceRef.current) clearTimeout(debounceRef.current); // pending list query is moot
    } else {
      // The list query is skipped while the board is shown, so filters may
      // have changed — always refetch page 0 on return to list.
      runQuery(0, true);
    }
  };

  const hasActiveFilters = !!(
    filters.q || filters.lifecycle || filters.eligibility || filters.milestone || filters.study ||
    filters.owner || filters.unassigned || filters.answerQuestion || filters.showArchived ||
    filters.showSnoozed || filters.condition || filters.labTest || filters.verifiedOnly
  );

  const mineActive = !!user?.id && filters.owner === user.id;
  const toggleUnassigned = () => apply({ unassigned: !filters.unassigned, owner: '' });
  const toggleMine = () =>
    mineActive ? apply({ owner: '' }) : apply({ owner: user?.id || '', unassigned: false });

  // Selection helpers
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const togglePage = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  // A bulk move to a negative disposition collects one reason for the whole
  // batch (the per-lead PATCH 400s without it).
  const [pendingBulkStatus, setPendingBulkStatus] = useState<string | null>(null);
  // A bulk move into a study-attributed positive (Screened / Randomized) needs
  // the "each lead keeps its own considering study" acknowledgement — the
  // per-lead PATCH 400s without study_confirmed (no single study can be right
  // for a whole batch).
  const [pendingBulkStudyStatus, setPendingBulkStudyStatus] = useState<string | null>(null);

  /** PATCH each selected contact sequentially; count (don't abort on) failures. */
  const bulkPatch = async (body: { owner_user_id: string | null } | { lifecycle_status: string; reason_code?: string; disposition_note?: string; study_confirmed?: boolean }) => {
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    setBulkError(null);
    setBulkProgress({ done: 0, total: ids.length });
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await api.patch(`/api/crm/contacts/${ids[i]}`, body);
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkBusy(false);
    setBulkProgress(null);
    if (failed > 0) setBulkError(`${failed} of ${ids.length} update${ids.length === 1 ? '' : 's'} failed. The rest were applied.`);
    setSelected(new Set());
    notifyStatsChanged(); // sidebar badges refetch immediately
    await Promise.all([runQuery(0, true), loadStats()]);
  };

  // Stats refresh after inline assignment is debounced: rapid triage would
  // otherwise re-fire the (fan-out) stats endpoint once per row.
  const statsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onOwnerChanged = (id: string, owner: string | null) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, owner_user_id: owner } : r)));
    if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
    statsDebounceRef.current = setTimeout(loadStats, 1500);
  };
  useEffect(() => () => { if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current); }, []);

  /**
   * Per-row quick archive/unarchive (mirrors Conversations.tsx toggleRowArchived,
   * simplified — the inbox has no conversation-tab counts to shuffle). In the
   * inbox the archived row leaves the working list (it requests
   * hide_archived=true), so remove it optimistically; in the database mode
   * archived rows stay visible, so flip the gray chip in place instead.
   */
  const archiveRow = async (row: ContactListItem, archive: boolean) => {
    setError(null);
    const wasLen = rows.length;
    if (isDatabase) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, archived_at: archive ? new Date().toISOString() : null } : r,
        ),
      );
    } else {
      setRows((prev) => prev.filter((r) => r.id !== row.id)); // optimistic remove
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { archived: archive });
      notifyStatsChanged(); // sidebar badges
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500); // tab counts
      // Don't strand the recruiter on an empty page while more rows exist.
      if (!isDatabase && wasLen - 1 === 0 && hasMore) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert: re-fetch page 0
      setError(errMsg(e));
    }
  };

  /**
   * Per-row snooze: defer a lead to a future time (or un-snooze with null).
   * When hiding (a future time with "Show snoozed" off) the row leaves the
   * working list, mirroring archiveRow; otherwise snoozed_until updates in place
   * (so the icon + chip reflect it in the "Show snoozed" view).
   */
  const snoozeRow = async (row: ContactListItem, untilIso: string | null) => {
    setError(null);
    const wasLen = rows.length;
    const hiding = isActiveSnooze(untilIso) && !filters.showSnoozed;
    if (hiding) {
      setRows((prev) => prev.filter((r) => r.id !== row.id)); // leaves the working list
    } else {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, snoozed_until: untilIso } : r)));
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { snoozed_until: untilIso });
      notifyStatsChanged(); // sidebar badges
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500); // tab counts
      if (hiding && wasLen - 1 === 0 && hasMore) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert
      setError(errMsg(e));
    }
  };

  // Close the test-tools menu on outside click.
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [toolsOpen]);

  const seedFlows = async () => {
    setSeeding(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/seed-default-flows');
      const flows = res.data?.flows || [];
      const created = flows.filter((f: any) => f.created).length;
      setSyncMsg(`Seeded automation flows: ${created} created, ${flows.length - created} already existed. Run reconciliation to enroll crm-test leads.`);
    } catch (e: any) {
      setSyncMsg(`Seed failed: ${errMsg(e)}`);
    } finally {
      setSeeding(false);
    }
  };

  const runSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/run-reconciliation');
      const d = res.data || {};
      setSyncMsg(`Synced ${d.total ?? 0} lead(s): ${d.created ?? 0} created, ${d.linked ?? 0} linked, ${d.flagged ?? 0} flagged, ${d.enrolled ?? 0} enrolled${d.errors ? `, ${d.errors} error(s)` : ''}.`);
      refresh();
    } catch (e: any) {
      setSyncMsg(`Sync failed: ${errMsg(e)}`);
    } finally {
      setSyncing(false);
    }
  };

  const runTick = async () => {
    setTicking(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/run-automation-tick');
      const d = res.data || {};
      setSyncMsg(`Automation tick: claimed ${d.claimed ?? 0}, advanced ${d.advanced ?? 0}, SMS enqueued ${d.enqueuedSms ?? 0}, completed ${d.completed ?? 0}${d.errors ? `, ${d.errors} error(s)` : ''}.`);
    } catch (e: any) {
      setSyncMsg(`Tick failed: ${errMsg(e)}`);
    } finally {
      setTicking(false);
    }
  };

  const anyToolBusy = seeding || syncing || ticking;
  const ownerSelectValue = filters.unassigned ? UNASSIGNED : filters.owner;

  const inputCls =
    'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';
  const menuItemCls =
    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700';
  const thCls = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
  const checkboxCls =
    'h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700';

  const tabs: Array<{ value: string; label: string; count: number | null }> = [
    { value: '', label: 'All', count: stats ? stats.total : null },
    ...LIFECYCLES.map((l) => ({
      value: l.value,
      label: l.label,
      count: stats ? (stats.by_status?.[l.value] ?? 0) : null,
    })),
  ];

  // ---- Unified filter system (shared <FilterBar>) ----
  const studyLabel = (slug: string) => {
    const s = studies.find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) : prettySlug(slug);
  };
  const ownerLabel = (id: string) => team.find((m) => m.id === id)?.name || 'Owner';
  const answerActive = !!(filters.answerQuestion.trim() && filters.answerValue.trim());

  // Secondary filters, revealed by the "Add filter" popover.
  const moreFilters: FilterDef[] = [
    {
      key: 'study',
      label: 'Study',
      value: filters.study,
      onChange: (v) => apply({ study: v }),
      options: [
        { value: '', label: 'Any study' },
        ...studies.map((s) => ({ value: s.slug, label: `${studyInternalLabel(s)} (${s.slug}) — ${s.count}` })),
      ],
    },
    {
      key: 'owner',
      label: 'Owner',
      value: ownerSelectValue,
      onChange: (v) => {
        if (v === UNASSIGNED) apply({ owner: '', unassigned: true });
        else apply({ owner: v, unassigned: false });
      },
      options: [
        { value: '', label: 'Any owner' },
        { value: UNASSIGNED, label: 'Unassigned' },
        ...team.map((m) => ({ value: m.id, label: m.name })),
      ],
    },
    {
      key: 'eligibility',
      label: 'Incoming eligibility',
      value: filters.eligibility,
      onChange: (v) => apply({ eligibility: v }),
      title: "Best landing-form verdict across the lead's studies. Manually-added contacts (no form submission) aren't matched.",
      options: [{ value: '', label: 'Any eligibility' }, ...INCOMING_ELIGIBILITY_OPTIONS],
    },
    {
      key: 'milestone',
      label: 'Milestone',
      value: filters.milestone,
      onChange: (v) => apply({ milestone: v }),
      title: "The pertinent-milestone sub-line shown under each lead's status.",
      options: [
        { value: '', label: 'Any milestone' },
        ...MILESTONE_FILTER_OPTIONS.map((k) => ({ value: k as string, label: MILESTONE_LABELS[k] })),
      ],
    },
    {
      type: 'custom',
      key: 'answer',
      label: 'Answer filter (lead_answers)',
      node: (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={filters.answerQuestion}
              onChange={(e) => apply({ answerQuestion: e.target.value }, true)}
              placeholder="question_id (e.g. ckd_q_age)"
              aria-label="Answer filter question id"
              className={`${inputCls} min-w-[8rem] flex-1`}
            />
            <select
              value={filters.answerOp}
              onChange={(e) => apply({ answerOp: e.target.value })}
              aria-label="Answer filter operator"
              className={`${inputCls} w-auto cursor-pointer`}
            >
              {ANSWER_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
            <input
              value={filters.answerValue}
              onChange={(e) => apply({ answerValue: e.target.value }, true)}
              placeholder="value (e.g. 60)"
              aria-label="Answer filter value"
              className={`${inputCls} w-20`}
            />
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Numeric operators compare numbers; eq/neq infer yes/no, number, or text; contains matches text.
          </p>
        </div>
      ),
    },
    {
      type: 'custom',
      key: 'condition',
      label: 'Medical condition',
      node: (
        <div>
          <input
            value={filters.condition}
            onChange={(e) => apply({ condition: e.target.value }, true)}
            placeholder="e.g. type 2 diabetes or condition.t2dm"
            aria-label="Medical condition filter"
            className={`${inputCls} w-full`}
          />
          <p className="mt-1 text-xs text-gray-400">
            Matches a diagnosis on the profile (name, or a precise profile key like condition.t2dm) — including ones a patient answered on a form.
          </p>
        </div>
      ),
    },
    {
      type: 'custom',
      key: 'lab',
      label: 'Lab / vital value',
      node: (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={filters.labTest}
              onChange={(e) => apply({ labTest: e.target.value }, true)}
              placeholder="test (e.g. bmi)"
              aria-label="Lab test name"
              className={`${inputCls} min-w-[6rem] flex-1`}
            />
            <select
              value={filters.labOp}
              onChange={(e) => apply({ labOp: e.target.value })}
              aria-label="Lab operator"
              className={`${inputCls} w-auto cursor-pointer`}
            >
              {LAB_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
            <input
              value={filters.labValue}
              onChange={(e) => apply({ labValue: e.target.value }, true)}
              placeholder="value (e.g. 30)"
              aria-label="Lab value"
              className={`${inputCls} w-20`}
            />
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Numeric compare on a profile lab/vital (e.g. bmi &gt; 30). Leave the value blank to match anyone with the test.
          </p>
        </div>
      ),
    },
    {
      type: 'custom',
      key: 'verified_only',
      label: 'Fact source',
      node: (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={filters.verifiedOnly}
            onChange={(e) => apply({ verifiedOnly: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Only recruiter-verified facts (condition / lab)
        </label>
      ),
    },
  ];

  // Applied-filter summary chips. The visible quick chips already cover
  // Mine / Unassigned / Show archived / Show snoozed, so those are omitted here.
  const activeChips: ActiveChip[] = [];
  if (filters.study) activeChips.push({ key: 'study', label: `Study: ${studyLabel(filters.study)}`, onRemove: () => apply({ study: '' }) });
  // Owner is suppressed when it's the current user — that case is the "Mine" quick chip.
  if (filters.owner && filters.owner !== user?.id) activeChips.push({ key: 'owner', label: `Owner: ${ownerLabel(filters.owner)}`, onRemove: () => apply({ owner: '' }) });
  if (filters.eligibility) {
    const el = INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === filters.eligibility);
    activeChips.push({ key: 'eligibility', label: `Eligibility: ${el?.label || filters.eligibility}`, onRemove: () => apply({ eligibility: '' }) });
  }
  if (filters.milestone) {
    activeChips.push({
      key: 'milestone',
      label: `Milestone: ${MILESTONE_LABELS[filters.milestone as keyof typeof MILESTONE_LABELS] || filters.milestone}`,
      onRemove: () => apply({ milestone: '' }),
    });
  }
  if (answerActive) {
    activeChips.push({
      key: 'answer',
      label: `Answer: ${filters.answerQuestion} ${filters.answerOp} ${filters.answerValue}`,
      onRemove: () => apply({ answerQuestion: '', answerValue: '' }),
    });
  }
  if (filters.condition.trim()) {
    activeChips.push({ key: 'condition', label: `Condition: ${filters.condition}`, onRemove: () => apply({ condition: '' }) });
  }
  if (filters.labTest.trim()) {
    const valPart = filters.labValue.trim() ? ` ${filters.labOp} ${filters.labValue}` : '';
    activeChips.push({ key: 'lab', label: `Lab: ${filters.labTest}${valPart}`, onRemove: () => apply({ labTest: '', labValue: '' }) });
  }
  if (filters.verifiedOnly) {
    activeChips.push({ key: 'verified_only', label: 'Verified facts only', onRemove: () => apply({ verifiedOnly: false }) });
  }

  // The "Add filter" badge mirrors exactly the secondary filters surfaced as
  // chips below — quick toggles (Mine / Unassigned) are intentionally not counted.
  const moreFiltersCount = activeChips.length;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={isDatabase ? Users : Inbox}
        title={isDatabase ? 'Patient database' : 'Leads'}
        count={stats ? stats.total : null}
        subtitle={isDatabase ? 'Everyone who has ever come through — search by name, phone, or email.' : undefined}
        actions={
          <>
            <button
              onClick={() => setShowNewLead(true)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">New lead</span>
            </button>
            {!isDatabase && (
              <SegmentedControl
                label="Inbox view"
                value={view}
                onChange={(v) => switchView(v as 'list' | 'board')}
                options={[
                  { value: 'list', label: 'List', icon: <List className="h-4 w-4" /> },
                  { value: 'board', label: 'Board', icon: <LayoutGrid className="h-4 w-4" /> },
                ]}
              />
            )}
            <RefreshButton onClick={refresh} loading={loading} label="Refresh leads" />
            {isSuperAdmin && !isDatabase && (
              <div ref={toolsRef} className="relative">
                <button
                  onClick={() => setToolsOpen((s) => !s)}
                  aria-expanded={toolsOpen}
                  aria-haspopup="menu"
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  {anyToolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                  Test tools
                  <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${toolsOpen ? 'rotate-180' : ''}`} />
                </button>
                {toolsOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 z-20 mt-2 w-60 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                  >
                    <button role="menuitem" onClick={() => { setToolsOpen(false); seedFlows(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Workflow className="h-4 w-4" />}
                      {seeding ? 'Seeding…' : 'Seed flows'}
                    </button>
                    <button role="menuitem" onClick={() => { setToolsOpen(false); runSync(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      {syncing ? 'Syncing…' : 'Run reconciliation'}
                    </button>
                    <button role="menuitem" onClick={() => { setToolsOpen(false); runTick(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {ticking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {ticking ? 'Running…' : 'Run automation tick'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        }
      />

      {syncMsg && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {syncMsg}
        </div>
      )}

      {/* The recruiter's own follow-ups (renders nothing when none are open) */}
      {!isDatabase && <MyTasksPanel />}

      {/* Pipeline tabs (inbox list view only — board columns replace them) */}
      {!isDatabase && !isBoard && (
        <SegmentedTabs
          label="Lead pipeline"
          tabs={tabs}
          value={filters.lifecycle}
          onChange={(v) => apply({ lifecycle: v })}
        />
      )}

      {/* Toolbar: search + quick filters + Add-filter popover + active-filter chips */}
      <FilterBar active={<ActiveFilters chips={activeChips} onClearAll={hasActiveFilters ? clearFilters : undefined} />}>
        <SearchInput
          value={filters.q}
          onChange={(v) => apply({ q: v }, true)}
          placeholder="Search name, phone, or email…"
          label="Search leads"
          className="flex-1 basis-56"
        />
        {!isDatabase && (
          <FilterChip active={filters.unassigned} onClick={toggleUnassigned}>
            Unassigned{stats && stats.unassigned > 0 ? ` (${stats.unassigned.toLocaleString()})` : ''}
          </FilterChip>
        )}
        {!!user?.id && <FilterChip active={mineActive} onClick={toggleMine}>Mine</FilterChip>}
        {/* Inbox only (list + board). On the list this flips to the archived-only
            set (conversation=archived); on the board it adds archived cards into
            the columns. Both refresh stats so the badges track the visible rows. */}
        {!isDatabase && (
          <FilterChip
            active={filters.showArchived}
            onClick={() => { apply({ showArchived: !filters.showArchived }); loadStats(); }}
            icon={<Archive className="h-4 w-4" />}
          >
            Show archived
          </FilterChip>
        )}
        {!isDatabase && !isBoard && (
          <FilterChip
            active={filters.showSnoozed}
            onClick={() => { apply({ showSnoozed: !filters.showSnoozed }); loadStats(); }}
            icon={<MoonStar className="h-4 w-4" />}
          >
            Show snoozed
          </FilterChip>
        )}
        <FilterSelect value={filters.sort} onChange={(v) => apply({ sort: v })} auto label="Sort order">
          <option value="">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="activity">Recent activity</option>
          <option value="eligibility">By eligibility</option>
        </FilterSelect>
        <AddFilterMenu filters={moreFilters} activeCount={moreFiltersCount} />
      </FilterBar>

      {mergeCandidates.length > 0 && (
        <button
          type="button"
          onClick={() => setShowMergeModal(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-left text-sm text-yellow-800 transition-colors duration-150 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">
            {mergeCandidates.length} pending merge candidate{mergeCandidates.length === 1 ? '' : 's'} (phone/email pointed to different contacts — flagged for review, not auto-merged).
          </span>
          <span className="flex-shrink-0 font-medium underline">Review</span>
        </button>
      )}

      {showNewLead && (
        <NewLeadModal
          studies={studies}
          team={team}
          onClose={() => setShowNewLead(false)}
          onCreated={handleLeadCreated}
        />
      )}

      {showMergeModal && (
        <MergeCandidatesModal
          candidates={mergeCandidates}
          onClose={() => setShowMergeModal(false)}
          onResolved={(id) => {
            // Optimistic removal for instant feedback; auto-close when the queue
            // empties. A server reconcile follows because one merge can also sweep
            // sibling candidates that referenced the same loser.
            setMergeCandidates((prev) => {
              const next = prev.filter((c) => c.id !== id);
              if (next.length === 0) setShowMergeModal(false);
              return next;
            });
            loadMergeCandidates();
            // A merge moves children onto the kept contact — refresh the list/stats.
            runQuery(0, true);
            loadStats();
          }}
        />
      )}

      {!isBoard && truncated && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          This filter matched more contacts than the current cap — showing a partial set. Narrow the filter to see all matches.
        </div>
      )}

      {!isBoard && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      {/* Bulk actions (list view only — board has no selection) */}
      {!isBoard && (selected.size > 0 || bulkBusy) && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20">
          <span className="text-sm font-medium text-primary-800 dark:text-primary-300">{selected.size} selected</span>
          <select
            value=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              bulkPatch({ owner_user_id: v === UNASSIGNED ? null : v });
            }}
            aria-label="Assign selected leads to"
            className={`${inputCls} w-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <option value="">Assign to…</option>
            <option value={UNASSIGNED}>Unassigned</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <select
            value=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              // Negative dispositions need a reason — collect one for the batch.
              if (FINAL_DISPOSITIONS.includes(v)) { setPendingBulkStatus(v); return; }
              // Study-attributed positives need the per-lead-study acknowledgement.
              if (STUDY_ATTRIBUTED_STAGES.includes(v)) { setPendingBulkStudyStatus(v); return; }
              bulkPatch({ lifecycle_status: v });
            }}
            aria-label="Set status for selected leads"
            className={`${inputCls} w-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <option value="">Set status…</option>
            {LIFECYCLES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          {pendingBulkStatus && (
            <DispositionReasonModal
              status={pendingBulkStatus}
              askStudy={STUDY_ATTRIBUTED_STAGES.includes(pendingBulkStatus)}
              bulkCount={selected.size}
              onCancel={() => setPendingBulkStatus(null)}
              onConfirm={(reasonCode, note) => {
                const status = pendingBulkStatus;
                setPendingBulkStatus(null);
                const body: { lifecycle_status: string; reason_code: string; disposition_note?: string; study_confirmed?: boolean } = {
                  lifecycle_status: status, reason_code: reasonCode,
                };
                if (note) body.disposition_note = note;
                // Bulk can't pick one study for a batch — each lead keeps its
                // own considering study (acknowledged in the modal).
                if (STUDY_ATTRIBUTED_STAGES.includes(status)) body.study_confirmed = true;
                void bulkPatch(body);
              }}
            />
          )}
          {pendingBulkStudyStatus && (
            <StageStudyConfirmModal
              status={pendingBulkStudyStatus}
              bulkCount={selected.size}
              onCancel={() => setPendingBulkStudyStatus(null)}
              onConfirm={() => {
                const status = pendingBulkStudyStatus;
                setPendingBulkStudyStatus(null);
                void bulkPatch({ lifecycle_status: status, study_confirmed: true });
              }}
            />
          )}
          {bulkBusy && bulkProgress && (
            <span className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              Updating {bulkProgress.done}/{bulkProgress.total}
            </span>
          )}
          {!bulkBusy && (
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
      )}

      {!isBoard && bulkError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{bulkError}</div>
      )}

      {/* Results */}
      {isBoard ? (
        <KanbanBoard
          filters={filters}
          stats={stats}
          refreshToken={boardRefresh}
          onMoved={() => {
            // Same debounce as inline owner changes: rapid drags shouldn't
            // re-fire the fan-out stats endpoint once per card.
            if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
            statsDebounceRef.current = setTimeout(loadStats, 1500);
          }}
        />
      ) : loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          {isDatabase ? (
            <Users className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          ) : (
            <Inbox className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          )}
          <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {isDatabase ? 'No contacts match' : 'No leads match'}
          </h3>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? 'Try clearing or broadening your filters.'
              : isDatabase
                ? 'Patients will appear here as leads come in.'
                : 'New leads will appear here as they come in.'}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <X className="h-4 w-4" /> Clear filters
            </button>
          ) : (
            <button
              onClick={() => setShowNewLead(true)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <UserPlus className="h-4 w-4" /> New lead
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 shadow-sm dark:border-gray-700 sm:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }}
                        onChange={togglePage}
                        aria-label="Select all leads on this page"
                        className={checkboxCls}
                      />
                    </th>
                    {['Lead', 'Study', 'Status', 'Owner', 'Last activity', 'Arrival'].map((h) => (
                      <th key={h} className={thCls}>{h}</th>
                    ))}
                    <th className="w-10 px-4 py-3" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {rows.map((c) => {
                    const reply = needsReply(c.last_comm);
                    const contactInfo = [c.phone_e164 ? formatPhone(c.phone_e164) : null, c.email].filter(Boolean).join(' · ');
                    const source = sourceOf(c);
                    return (
                      <tr
                        key={c.id}
                        tabIndex={0}
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                        onKeyDown={(e) => {
                          // Only when the row itself is focused — Enter on an
                          // embedded checkbox/select must not navigate away.
                          if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`);
                        }}
                        className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(c.id)}
                            onChange={() => toggleRow(c.id)}
                            aria-label={`Select ${contactName(c)}`}
                            className={checkboxCls}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              title={contactName(c)}
                              className={`max-w-[16rem] truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}
                            >
                              {contactName(c)}
                            </span>
                            {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                            {c.sms_opt_out && (
                              <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">opted out</span>
                            )}
                            {c.archived_at && (
                              <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">archived</span>
                            )}
                            {!isDatabase && !filters.showArchived && isActiveSnooze(c.snoozed_until) && (
                              <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
                            )}
                          </div>
                          {contactInfo && (
                            <div className="mt-0.5 max-w-[16rem] truncate text-xs text-gray-500 dark:text-gray-400">{contactInfo}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StudyChips studies={c.studies} />
                          {source && <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{source}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <LifecycleBadge status={c.lifecycle_status} />
                          {c.milestone && (
                            <div className="mt-1"><MilestoneChip milestone={c.milestone} /></div>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <OwnerSelect
                            contactId={c.id}
                            value={c.owner_user_id}
                            compact
                            onChanged={(o) => onOwnerChanged(c.id, o)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {c.last_comm ? (
                            <>
                              <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                <ChannelIcon channel={c.last_comm.channel} />
                                {relTime(c.last_comm.occurred_at)}
                              </div>
                              {c.last_comm.body && (
                                <div className="mt-0.5 max-w-[16rem] truncate text-xs text-gray-400 dark:text-gray-500">{c.last_comm.body}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td
                          className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400"
                          title={arrivalOf(c) ? new Date(arrivalOf(c)!).toLocaleString() : undefined}
                        >
                          {dateTime(arrivalOf(c))}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            {!isDatabase && !filters.showArchived && (
                              <SnoozeButton
                                snoozedUntil={c.snoozed_until}
                                onSnooze={(iso) => snoozeRow(c, iso)}
                                label={contactName(c)}
                              />
                            )}
                            <button
                              onClick={() => archiveRow(c, !c.archived_at)}
                              aria-label={c.archived_at ? `Unarchive ${contactName(c)}` : `Archive ${contactName(c)}`}
                              title={c.archived_at ? 'Unarchive' : 'Archive'}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
                            >
                              {c.archived_at ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {rows.map((c) => {
              const reply = needsReply(c.last_comm);
              const contactInfo = [c.phone_e164 ? formatPhone(c.phone_e164) : null, c.email].filter(Boolean).join(' · ');
              const source = sourceOf(c);
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/crm/contacts/${c.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`);
                  }}
                  className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className={`truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}>
                        {contactName(c)}
                      </span>
                      {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                      {c.sms_opt_out && (
                        <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">opted out</span>
                      )}
                      {c.archived_at && (
                        <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">archived</span>
                      )}
                      {!isDatabase && !filters.showArchived && isActiveSnooze(c.snoozed_until) && (
                        <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
                      )}
                    </span>
                    <span className="flex flex-shrink-0 flex-col items-end gap-1">
                      <LifecycleBadge status={c.lifecycle_status} />
                      {c.milestone && <MilestoneChip milestone={c.milestone} />}
                    </span>
                  </div>
                  {contactInfo && (
                    <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">{contactInfo}</div>
                  )}
                  {(c.studies || []).length > 0 && (
                    <div className="mt-2"><StudyChips studies={c.studies} /></div>
                  )}
                  {c.last_comm && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <ChannelIcon channel={c.last_comm.channel} />
                      <span className="flex-shrink-0">{relTime(c.last_comm.occurred_at)}</span>
                      {c.last_comm.body && <span className="truncate">{c.last_comm.body}</span>}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <OwnerSelect
                        contactId={c.id}
                        value={c.owner_user_id}
                        compact
                        onChanged={(o) => onOwnerChanged(c.id, o)}
                      />
                      {!isDatabase && !filters.showArchived && (
                        <SnoozeButton
                          snoozedUntil={c.snoozed_until}
                          onSnooze={(iso) => snoozeRow(c, iso)}
                          label={contactName(c)}
                        />
                      )}
                      <span onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => archiveRow(c, !c.archived_at)}
                          aria-label={c.archived_at ? `Unarchive ${contactName(c)}` : `Archive ${contactName(c)}`}
                          title={c.archived_at ? 'Unarchive' : 'Archive'}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
                        >
                          {c.archived_at ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                        </button>
                      </span>
                    </span>
                    <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {source ? `${source} · ` : ''}arr {dateTime(arrivalOf(c))}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
